import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { withRouter } from "tools/routerCompat";
import { RouteComponentProps } from "tools/routerCompat.d";
import { MCard, MGrid, MSpinner, MSplitter } from "components/Shared";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import "styles/components/CallCenter/PhoneSupportForm/PhoneSupportForm.scss"
import Button from "@mui/material/Button";
import { getAllRoleData } from "requests/internetUsers";
import { MCardControlMethods, MCardField, MCardProps } from "components/Shared/MCard";
import { SearchResultDto } from "interfaces/BusinessModelsExtensions";
import { EmployeeListDto, EmployeeRoleEnum, FieldServicesCaseResult, NameNumberDto, PayPeriodDto, PhoneSupportCallAddRequest, PhoneSupportCategoryDto, QaAgentAuditResultResponse, RoleDto } from "interfaces/Mueller.Business.Models";
import { getEmployeesByRoles } from "requests/employee/index";
import { BUSOP_AGENT, CUSTOMER_EXPERIENCE_REP, FIELD_SERVICE_REP, QUALITY_COMPLIANCE_AUDITOR} from "typeConstants/employeeRoles";
import MCaseView from "containers/Homeowner/MCaseView";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { addPhoneSupportCall, getPhoneSupportCategories } from "requests/phoneSupportCall";
import { appendHeader, clearHeaderDefinitions } from "actions/layout";
import { connect } from "react-redux";
import * as yup from "yup";
import { normalizePhone } from "components/FormElements";
import { userHasRight } from "selectors/rights";
import { CLOCK_IN_OR_OUT_FUNCTION, CUSTOMER_EXPERIENCE_COACHING_CALL, QA_PHONE_SUPPORT_COACHING_CALL } from "typeConstants/rights";
import { InitialState } from "reducers/initialState";
import { getAssignedCasesByEmployeeId } from "requests/inspectorCases";
import { getUserAuditResults } from "requests/qaCaseAudit";
import { getCasesInQueue } from "requests/qualityAssurance";
import { COL_CODE_ADDRESS, COL_CODE_CONTROL_NUMBER, COL_CODE_CUSTOMER_NAME } from "components/Shared/MGrid";
import { FieldLevelCoordinatorCasePartial } from "./PhoneSupportForm";
import QualityAssuranceTimeLogDetails from "components/EmployeeTimeLogs/QualityAssuranceTimeLogDetails";
import { getPayPeriodList } from "actions/casetimelog";
import SchedulingAssistantTimeLogDetails from "../SchedulingAssistant/SchedulingAssistantTimeLogDetails";
import useUnsavedChangesBlocker from "hooks/useUnsavedChangesBlocker";

interface Props extends RouteComponentProps  {
  appendHeader: (headerDefinitions: any) => void,
  clearHeaderDefinitions: (headerDefinitions: any) => void,
  canAddCustomerExperienceCoachingCall: boolean,
  canAddQaPhoneSupportCoachingCall: boolean,
  canClockInOut: boolean,
  activeClock: InitialState["user"]["activeClock"],
  getPayPeriodList: () => void,
  payPeriods: PayPeriodDto[],
};

interface RoleMap {
  roleName: string,
  employeeRoleCode: number,
  hasRight: boolean,
}

const saveButtonForClear = { name: "Save" };
const returnToPayrollSummaryForClear = { name: "Return to Payroll Summary" };
const moreThanOneCallerRole = (callerRoles: any[]) => callerRoles.length > 1;

const clearAllButtonsList = [saveButtonForClear, returnToPayrollSummaryForClear];

const CoachingPhoneSupportForm = ({ appendHeader, clearHeaderDefinitions, history, canClockInOut, activeClock, payPeriods, getPayPeriodList, canAddCustomerExperienceCoachingCall, canAddQaPhoneSupportCoachingCall }: Props) => {
  const mounted = useRef(true);
  const handleSaveClicked = useRef<(() => void) | null>(null);
  const cardControlMethods = useRef<MCardControlMethods>(null);
  const dialog = useContext<DialogProviderValue>(DialogContext);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  useUnsavedChangesBlocker(hasUnsavedChanges);
  const allRolesToEmployeeRoleMaps: RoleMap[] = [
    { roleName: "Quality Review", employeeRoleCode: BUSOP_AGENT, hasRight: canAddQaPhoneSupportCoachingCall },
    { roleName: "Quality Compliance Auditor", employeeRoleCode: QUALITY_COMPLIANCE_AUDITOR, hasRight: canAddQaPhoneSupportCoachingCall },
    { roleName: "Customer Experience Rep", employeeRoleCode: CUSTOMER_EXPERIENCE_REP, hasRight: canAddCustomerExperienceCoachingCall }
  ];
  const roleToEmployeeRoleMap: RoleMap[] = allRolesToEmployeeRoleMaps.filter(r => r.hasRight);
  const includedRoleNames: string[] = roleToEmployeeRoleMap.filter(r => r.hasRight).map(r => r.roleName);

  // we need to be able to clear some data in the call card but cards werent built with that in mind while they are in edit mode
  // this allows us to randomize the card key when we need to update it, which will make it pull the data in its fields from the following state data again
  const [callCardKey, setCallCardKey] = useState("");

  const [roles, setRoles] = useState<RoleDto[]>(null);
  const [phoneSupportCategories, setPhoneSupportCategories] = useState<PhoneSupportCategoryDto[]>(null);
  const [employeesByEmployeeRoleId, setEmployeesByEmployeeRoleId] = useState<Record<EmployeeRoleEnum, EmployeeListDto[]>>();
  const [isSavingCall, setIsSavingCall] = useState(false);

  const [callRole, setCallRole] = useState<{ roleDto: RoleDto, employeeRoleCode: number}>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListDto>(null);
  const [callerName, setCallerName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<PhoneSupportCategoryDto>(null);

  // the calls we call dont all use FieldServicesCaseResult, but they all use different formats, so i just save the columns i need in the format of FieldServicesCaseResult, not nearly all columns will be filled
  const [employeeCaseQueue, setEmployeeCaseQueue] = useState<FieldServicesCaseResult[]>(null);

  const [notes, setNotes] = useState("");
  const [startedWorkingOnCall, setStartedWorkingOnCall] = useState<Date>(null);

  const [controlNumberLookup, setControlNumberLookup] = useState("");
  const [searchForControlNumber, setSearchForControlNumber] = useState<number>(null);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<PayPeriodDto>();
  
  let isLoadingCaseQueue = false;
  if (selectedEmployee && callRole && !employeeCaseQueue) isLoadingCaseQueue = true;

  useEffect(() => {
    getPayPeriodList();
    getAllRoleData((roles: RoleDto[]) => {
      setRoles(roles.filter(r => includedRoleNames.includes(r.name)));
      
      //If they have the right to make customer experience support calls, assume that should be first
      if(canAddCustomerExperienceCoachingCall) {
        let customerExperienceRole = roles.find(r => r.name === "Customer Experience Rep");
        setCallRole({roleDto: customerExperienceRole, employeeRoleCode: getEmployeeRoleFromRoleName(customerExperienceRole.name)})
      }
      else {
        let qualityReview = roles.find(r => r.name === "Quality Review");
        setCallRole({ roleDto: qualityReview, employeeRoleCode: getEmployeeRoleFromRoleName(qualityReview.name) }); 
      }
    }, mounted);
    getEmployeesByRoles(
      roleToEmployeeRoleMap.map(r => r.employeeRoleCode),
      employeeList => setEmployeesByEmployeeRoleId(employeeList.filter((e: EmployeeListDto) => !!e.internetUserId).reduce((acc, e: EmployeeListDto) => {
        if (!acc[e.roleId]) acc[e.roleId] = [];
        acc[e.roleId].push(e);
        return acc;
      }, {})),
      mounted);
    getPhoneSupportCategories(categories => {
      setPhoneSupportCategories(categories);
      setSelectedCategory(categories.find(c => c.code === "COACHING_CALL"));
    }, mounted);

    clearHeaderDefinitions(clearAllButtonsList);
    appendSaveHeaderButton();

    let needsClockIn = canClockInOut && !(activeClock?.clockInDate);

    if (needsClockIn) {
      dialog.show(
        "Please Clock In",
        "You are not currently clocked in. Calls cannot be logged until you are clocked in. Please navigate to the QA Dashboard page to clock in before starting your Call",
        [{ label: "QA Dashboard", onClick: () => history.push("/qa-dashboard"), isPrimary: true }],
        { width: 500 }
      );
    }

    return () => {
      mounted.current = false;
      clearHeaderDefinitions(clearAllButtonsList);
    };
  }, []);

  useEffect(() => {
    if (!selectedPayPeriod && payPeriods.length) {
      setSelectedPayPeriod(payPeriods[1]);
    }
  }, [payPeriods]);

  useEffect(() => {
    // if statement avoids this getting called on first render
    if (callRole || selectedEmployee || callerName || selectedCategory || notes || searchForControlNumber) {
      setHasUnsavedChanges(true);
    }
  }, [callRole, selectedEmployee, callerName, selectedCategory, notes, searchForControlNumber]);

  useEffect(() => {
    setEmployeeCaseQueue(null);
    if (selectedEmployee && callRole) getAssignedCases();
  }, [selectedEmployee?.employeeId, callRole?.employeeRoleCode]);

  const caseSort = (c1: FieldServicesCaseResult, c2: FieldServicesCaseResult) => c1.controlIDNumber - c2.controlIDNumber;

  const getAssignedCases = () => {
    if (callRole.roleDto.name === "Inspector") {
      getAssignedCasesByEmployeeId(selectedEmployee.employeeId, (result: SearchResultDto<FieldLevelCoordinatorCasePartial>) => {
        let cases = result.rows.map(c => ({
          controlIDNumber: c.controlIdNumber,
          inspectionType: c.inspectionType,
          address: c.address,
          customerName: c.customerName,
        } as FieldServicesCaseResult)).sort(caseSort);
        setEmployeeCaseQueue(cases);
      }, mounted);
    } else if (callRole.roleDto.name === "Quality Review") {
      getUserAuditResults({ internetUserId: selectedEmployee.internetUserId, lookbackDays: 14 }, (auditResults: QaAgentAuditResultResponse[]) => {
        getCasesInQueue(selectedEmployee.employeeId, (casesInQueue: FieldServicesCaseResult[]) => {
          let allCases = auditResults.map(c => ({
            controlIDNumber: c.controlIdNumber,
            inspectionType: c.surveyType,
            address: c.address,
            customerName: c.customerInfo.customerName,
          } as FieldServicesCaseResult)).concat(casesInQueue);
          allCases.sort(caseSort);
          setEmployeeCaseQueue(allCases);
        }, mounted);
      }, mounted);
    }
  };

  // see comment on callCardKey useState line
  const refreshCallCard = () => setCallCardKey(`key-${Math.random()}`);

  const getEmployeeRoleFromRoleName = (roleName: string) => roleToEmployeeRoleMap.find(r => r.roleName === roleName)?.employeeRoleCode;

  handleSaveClicked.current = () => {
    if (isSavingCall) return;
    
    if (!startedWorkingOnCall) {
      dialog.showOk("Can't Save Case", "You need to start a call before you can save it");
      return;
    }
    
    if (cardControlMethods.current?.runValidation(true)) {
      setIsSavingCall(true);
      setSearchForControlNumber(null);
      setSelectedEmployee(null);
      let curDate = new Date();
      let phoneSupportCall: PhoneSupportCallAddRequest = {
        supportedForRoleId: callRole.roleDto.id,
        phoneSupportCategoryId: selectedCategory.id,
        supportedEmployeeId: selectedEmployee.employeeId,
        supportedCallerName: selectedEmployee.firstLastName ?? callerName,
        startUtcDateTime: startedWorkingOnCall,
        completeUtcDateTime: curDate,
        timeZoneOffsetMinutes: curDate.getTimezoneOffset(),
        notes: notes,
        isCompleted: true,
      };

      addPhoneSupportCall(phoneSupportCall, () => {
        resetPageState();
        dialog.showOk(
          "Success",
          "Call Saved Successfully",
        );
      }, mounted);
    }
  };

  const resetPageState = () => {
    setIsSavingCall(false);
    setNotes("");
    setControlNumberLookup("");
    setSearchForControlNumber(null);
    refreshCallCard();
    setStartedWorkingOnCall(null);
    setEmployeeCaseQueue(null);
    setHasUnsavedChanges(false);
    setSelectedEmployee(null);
    setCallerName("");
  };

  const startCall = () => {
    setStartedWorkingOnCall(new Date());
  };

  const appendSaveHeaderButton = () => {
    appendHeader({
      buttons: [
        { name: saveButtonForClear.name, onClick: () => handleSaveClicked.current() }
      ],
    });
  };

  const appendReturnToPayrollSummary = () => {
    appendHeader({
      buttons: [
        { name: returnToPayrollSummaryForClear.name, onClick: onCasePaneClosed }
      ],
    });
  };

  const onCasePaneClosed = () => {
    setSearchForControlNumber(null);
    clearHeaderDefinitions(clearAllButtonsList);
    appendSaveHeaderButton();
    setControlNumberLookup("");
  };

  const onCallerRoleChange = (newRole) => {
      setCallRole({ roleDto: newRole.value, employeeRoleCode: getEmployeeRoleFromRoleName(newRole.value.name) });
      setSelectedEmployee(null);
      setCallerName(null);
      refreshCallCard();
    };

  const onSelectedEmployeeChange = (newEmployee) => {
    setSelectedEmployee(newEmployee.value);
  };

  const onNotesChange = (newNotes) => {
    setNotes(newNotes);
  };

  const onCaseSearchClicked = (controlNumberOverride?: number) => {
    let numericControlNumber: number;
    if (controlNumberOverride) {
      setControlNumberLookup(`${controlNumberOverride}`);
      numericControlNumber = controlNumberOverride;
    } else {
      numericControlNumber = +controlNumberLookup?.trim();
    }
    
    if (numericControlNumber) appendReturnToPayrollSummary();
    setSearchForControlNumber(numericControlNumber);
  };

  const mapPhoneCategory = (category: PhoneSupportCategoryDto) => category && ({ label: category.name, value: category });

  const mapEmployeeForDropdown = (e: EmployeeListDto) => ({ label: e.firstLastName + (e.phoneNumber ? ` (${normalizePhone(e.phoneNumber)})` : " (phone # not provided)"), value: e });

  let supportForm = <MSpinner />;

  if (roles && employeesByEmployeeRoleId && phoneSupportCategories && !isSavingCall) {

    let rolesArray = roles.map(r => ({ label: r.name, value: r }));

    // Add this helper so we never call .map on undefined
    const employeesForSelectedRole =
      employeesByEmployeeRoleId?.[callRole?.employeeRoleCode] ?? [];
    const employeeOptions = employeesForSelectedRole.map(mapEmployeeForDropdown);

    let callCardFields: MCardField[] = [
      {
        key: "callRole",
        label: "Caller Role",
        editable: moreThanOneCallerRole(roleToEmployeeRoleMap),
        value: rolesArray.find(r => r.value.name === callRole?.roleDto.name),
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: rolesArray, nullable: false },
        onChange: onCallerRoleChange,
      },
    ];

    if (includedRoleNames.includes(callRole?.roleDto.name)) {
      callCardFields.push({
        key: "employee",
        label: "Employee Calling",
        validation: yup.string().nullable().required("Employee Calling is required"),
        value: selectedEmployee && mapEmployeeForDropdown(selectedEmployee),
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: employeeOptions, nullable: false },
        onChange: onSelectedEmployeeChange,
      });
    } else {
      callCardFields.push({ key: "callerName", label: "Caller Name", validation: yup.string().nullable().required("Caller Name is required") });
    }

    callCardFields.push(
      {
        key: "category",
        label: "Category",
        validation: yup.string().nullable().required("Category is required"),
        value: mapPhoneCategory(selectedCategory),
        editable: false,
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: phoneSupportCategories.map(c => mapPhoneCategory(c)), nullable: false }
      },
      {
        key: "notes",
        label: "TEAM Note",
        validation: yup.string().nullable().required("TEAM Note is required"),
        value: notes,
        dataType: DATA_TYPE_MULTI_LINE_STRING,
        onChange: onNotesChange,
      }
    );

    let employeeCaseGrid: ReactNode = null;

    if (callRole && callRole.employeeRoleCode != CUSTOMER_EXPERIENCE_REP && selectedEmployee) {
      employeeCaseGrid = (
        <MGrid
          className="md-paper--1"
          isFetchingData={isLoadingCaseQueue}
          data={employeeCaseQueue}
          columnDefinitions={[
            { code: COL_CODE_CONTROL_NUMBER, dataSource: "controlIDNumber" },
            { key: "inspectionType", width: 270, name: "Inspection Type", dataSource: "inspectionType" },
            { code: COL_CODE_ADDRESS, dataSource: "address", name: "Address to Survey" },
            { code: COL_CODE_CUSTOMER_NAME, dataSource: "customerName" },
          ]}
          onRowClick={(index: number) => onCaseSearchClicked(employeeCaseQueue[index].controlIDNumber)}
        />
      );
    }

    supportForm = (
      <div className="phone-support-form">
        <MCard
          key={callCardKey}
          title="Call"
          onlyEditable
          fields={callCardFields}
          getControlMethods={controlMethods => cardControlMethods.current = controlMethods}
        />
        {employeeCaseGrid}
      </div>
    );
  }

  let secondChild: ReactNode = null;
  let secondChildTitle = "";

  if (searchForControlNumber) {
    secondChildTitle = `Case View for ${searchForControlNumber}`;
    // change the key because MCaseView probably isnt the best with control numbers getting switched out from under it
    secondChild = <MCaseView key={`${searchForControlNumber}`} controlNumberOverride={searchForControlNumber} />;
  } else if (selectedEmployee && selectedPayPeriod) {
    // this ambiguous datatype can be found in EmployeeTimelogs index.js [const employeeSelected = (id, data) => {...]
    // the QualityAssuranceTimeLogDetails control doesnt need the number/isTeamLeader so we currently dont go get it
    let selectedEmployeeMapped = {
      internetUserId: selectedEmployee.internetUserId,
      employeeId: selectedEmployee.employeeId,
      name: {
        firstName: selectedEmployee.firstName,
        lastName: selectedEmployee.lastName,
        firstLast: selectedEmployee.firstLastName,
        lastFirst: selectedEmployee.lastFirstName,
      } as NameNumberDto,
    };
    secondChildTitle = `Time Log for ${selectedEmployee.firstLastName}`;
  
    switch(callRole.employeeRoleCode) {
      case CUSTOMER_EXPERIENCE_REP:
        secondChild = 
          <SchedulingAssistantTimeLogDetails
            selectedPayPeriod={selectedPayPeriod}
            selectedEmployee={selectedEmployeeMapped} />;
        break;
      default:
        secondChild =
          <QualityAssuranceTimeLogDetails
            isTeamCardCollapsible
            hideAddTEAMNotes
            selectedPayPeriod={selectedPayPeriod}
            selectedEmployee={selectedEmployeeMapped} />;
    }
  }

  return (
    <div className="phone-support-form-container">
      {startedWorkingOnCall ?
      <MSplitter
        firstChild={supportForm}
        firstChildInitialSize="var(--phone-support-form-max-width)" // defined in PhoneSupportForm.scss
        secondChildTitle={{
          title: secondChildTitle,
          buttons: [
            { icon: "close", onClick: onCasePaneClosed }
          ]
        }}
        secondChild={secondChild}
      /> :
      <Button onClick={startCall}>Start</Button>
      }
    </div>
  );
};

const mapStateToProps = (state: InitialState) => ({
  canClockInOut: userHasRight(CLOCK_IN_OR_OUT_FUNCTION)(state),
  canAddCustomerExperienceCoachingCall: userHasRight(CUSTOMER_EXPERIENCE_COACHING_CALL)(state),
  canAddQaPhoneSupportCoachingCall: userHasRight(QA_PHONE_SUPPORT_COACHING_CALL)(state),
  activeClock: state.user.activeClock,
  payPeriods: state.caseTimeLog.payPeriods,
});

const mapDispatchToProps = dispatch => ({
  getPayPeriodList() {
    dispatch(getPayPeriodList());
  },
  appendHeader(headerDefinitions) {
    dispatch(appendHeader(headerDefinitions));
  },
  clearHeaderDefinitions(headerDefinitions) {
    dispatch(clearHeaderDefinitions({ buttons: headerDefinitions }));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CoachingPhoneSupportForm));
